import { useCallback, useEffect, useState } from "react";
import { TranslationService } from "../../../../../services/TranslationService";
import AIBillingService, {
  AIBillingErrorKind,
  AIBillingPaymentListResponse,
  AIBillingPeriodRequest,
  getErrorKind
} from "../../../../../services/AIBillingService";
import Paginator from "../../../../shared/Paginator";
import AIBillingStateBlock from "./AIBillingStateBlock";
import { formatIsoDate, formatSignedAmountUsd } from "../AIBillingFormat";

const RECHARGES_PAGE_SIZE = 5;

const AIBillingRechargeList = ({
  range
}: {
  range: AIBillingPeriodRequest;
}) => {
  const { translate } = TranslationService;

  const [data, setData] = useState<AIBillingPaymentListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<AIBillingErrorKind>("none");
  const [currentPage, setCurrentPage] = useState(0);

  const fetchPayments = useCallback(
    async (period: AIBillingPeriodRequest, page: number) => {
      setLoading(true);
      setError("none");
      const result = await AIBillingService.getPayments({
        ...period,
        page,
        pageSize: RECHARGES_PAGE_SIZE
      });
      setLoading(false);
      if (result instanceof Error) {
        setError(getErrorKind(result));
        setData(null);
        return;
      }
      setData(result);
    },
    []
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [range.from, range.to]);

  useEffect(() => {
    fetchPayments(range, currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPayments, currentPage, range.from, range.to]);

  const retry = useCallback(
    () => fetchPayments(range, currentPage),
    [fetchPayments, range, currentPage]
  );

  const items = data ? (data.items ?? data.list ?? []) : [];

  return (
    <AIBillingStateBlock
      loading={loading}
      error={error}
      isEmpty={data !== null && items.length === 0}
      onRetry={retry}
      emptyMessage={translate.AINoRechargesYet}
      height={180}
    >
      {data && (
        <>
          <div className="d-flex flex-column">
            {items.map((item, index) => (
              <div
                key={`${item.date}_${index}`}
                className="d-flex justify-content-between gap-2 py-2"
                style={{
                  borderBottom:
                    index === items.length - 1 ? "none" : "1px solid #f0f0f0"
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: "0.8125rem" }}>
                    {item.publicDescription || item.reference || "—"}
                  </div>
                  <div
                    className="d-flex align-items-center gap-2 mt-1"
                    style={{ fontSize: "0.75rem", color: "#888" }}
                  >
                    <span>{formatIsoDate(item.date)}</span>
                  </div>
                </div>
                <div
                  style={{
                    fontSize: "0.8125rem",
                    fontWeight: 500,
                    color: "#16a34a",
                    whiteSpace: "nowrap"
                  }}
                >
                  {formatSignedAmountUsd(item.amount)}
                </div>
              </div>
            ))}
          </div>
          <Paginator
            hideSummary
            pageCount={Math.ceil(data.itemCount / data.pageSize)}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            pageSize={RECHARGES_PAGE_SIZE}
            loading={loading}
            error={error !== "none"}
            response={{
              pageSize: data.pageSize,
              itemCount: data.itemCount
            }}
          />
        </>
      )}
    </AIBillingStateBlock>
  );
};

export default AIBillingRechargeList;
